<template>
	<div class="relative inline-block" v-click-outside="close">
		<div ref="button" @click="toggle">
			<slot name="toggle" :is-open="isOpen" />
		</div>

		<div
			v-if="isRendered"
			ref="menu"
			:class="menuClasses"
			:style="{ visibility: isOpen ? 'visible' : 'hidden' }"
			@click="menuClicked"
		>
			<slot :close="close" :toggle="toggle" :is-open="isOpen" />
		</div>

		<div
			v-if="isRendered && !compact"
			class="fixed inset-0 z-40 bg-black/20 md:hidden"
			@click="close"
		/>
	</div>
</template>

<script>
import { createPopper } from '@popperjs/core'
import { twMerge } from 'tailwind-merge'

const classString = value =>
	typeof value === 'string'
		? value
		: Array.isArray(value)
		? value.map(classString).join(' ')
		: Object.keys(value || {})
				.filter(key => value[key])
				.join(' ')

export default {
	name: 'ActionMenu',
	props: {
		compact: Boolean,
		disabled: Boolean,
		align: {
			type: String,
			default: 'bottom-start'
		},
		menuClass: {
			type: [String, Array, Object],
			default: null
		}
	},
	data() {
		return {
			isOpen: false,
			isRendered: false,
			popper: null
		}
	},
	computed: {
		menuClasses() {
			if (this.compact) {
				return twMerge(
					'fixed z-50 w-max rounded-lg border border-grey-200 bg-white py-2.5 font-sans text-sm font-normal text-grey-1000 shadow-[0_8px_16px_-4px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05)]',
					classString(this.menuClass)
				)
			}
			const classes = [
				'absolute z-50 min-w-[13rem] overflow-hidden rounded-xl border border-neutral-200 bg-white py-2 shadow-xl'
			]

			if (this.align.endsWith('end')) {
				classes.push('right-0')
			} else if (this.align.endsWith('start')) {
				classes.push('left-0')
			} else {
				classes.push('left-1/2 -translate-x-1/2')
			}

			if (this.menuClass) {
				classes.push(this.menuClass)
			}

			return classes
		}
	},
	watch: {
		disabled(value) {
			if (value) this.close()
		}
	},
	methods: {
		toggle() {
			if (this.disabled) return
			if (this.isRendered) {
				this.close()
				return
			}

			this.open()
		},
		open() {
			if (this.disabled || this.isRendered) return
			this.isRendered = true
			this.$nextTick(() => {
				if (!this.isRendered) return
				this.destroyPopper()
				this.popper = createPopper(this.$refs.button, this.$refs.menu, {
					placement: this.align,
					strategy: this.compact ? 'fixed' : 'absolute',
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [0, 4]
							}
						},
						{
							name: 'preventOverflow',
							options: {
								padding: 12
							}
						},
						{
							name: 'flip',
							options: {
								padding: 12
							}
						}
					]
				})
				this.popper.forceUpdate()
				requestAnimationFrame(() => {
					if (!this.isRendered) {
						return
					}

					this.isOpen = true
					this.$emit('dropdownIsOpen', true)
				})
			})
		},
		close() {
			this.isOpen = false
			this.isRendered = false
			this.$emit('dropdownIsOpen', false)
			this.destroyPopper()
		},
		menuClicked(event) {
			if (event.target.closest('[data-action-menu-item]')) {
				this.close()
			}
		},
		destroyPopper() {
			if (this.popper) {
				this.popper.destroy()
				this.popper = null
			}
		}
	},
	beforeDestroy() {
		this.destroyPopper()
	}
}
</script>
